import { useId } from 'react';
import { Check } from 'lucide-react';
import './conversion.css';

export type CompactPackOption = {
  qty: number;
  supply: string;
  unitsLabel: string;
  priceLabel: string;
  /** Numeric price (INR) — used for per-day and savings math. */
  price: number;
  /** Numeric compare-at price (INR), shown struck through when above price. */
  compareAt?: number;
  note?: string;
  disabled?: boolean;
};

const daysForQty = (qty: number) => qty * 30;
const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;

/** Card-style pack picker (30/60/90 days) with per-day price and savings note. */
export function CompactPackSelector({ options, value, onChange }: { options: CompactPackOption[]; value: number; onChange: (qty: number) => void }) {
  const group = useId();
  if (!options.length) return null;
  const perDay = (o: CompactPackOption) => o.price / daysForQty(o.qty);
  const bestQty = options.reduce((a, b) => (perDay(b) < perDay(a) ? b : a)).qty;
  const single = options.find((o) => o.qty === 1);
  const selected = options.find((o) => o.qty === value);
  const savings = single && selected && selected.qty > 1 ? Math.round(single.price * selected.qty - selected.price) : 0;
  return (
    <fieldset className="twc-packs">
      <legend>Choose your routine</legend>
      <div className="twc-pack-cards" role="radiogroup" aria-label="Choose your pack size">
        {options.map((option) => {
          const active = value === option.qty;
          return (
            <label key={option.qty} className={`twc-pack-card${active ? ' is-active' : ''}`}>
              {option.qty === bestQty && <span className="twc-pack-badge">Best daily value</span>}
              <input type="radio" name={group} value={option.qty} checked={active} disabled={option.disabled} onChange={() => onChange(option.qty)} />
              <span className="twc-pack-card-top">
                <strong>{daysForQty(option.qty)} days</strong>
                <span className="twc-pack-radio" aria-hidden="true"><span /></span>
              </span>
              <small>{option.unitsLabel}</small>
              <b>{option.priceLabel}</b>
              {option.compareAt && option.compareAt > option.price && <s>{inr(option.compareAt)}</s>}
              <em>₹{perDay(option).toFixed(2)}/day</em>
              {option.note && <small className="twc-pack-note">{option.note}</small>}
            </label>
          );
        })}
      </div>
      {savings > 0 && single && selected && (
        <p className="twc-pack-save">
          <Check size={14} strokeWidth={2.5} aria-hidden="true" />
          Save {inr(savings)} vs {selected.qty} separate 30-day packs.
        </p>
      )}
    </fieldset>
  );
}
